import { useState } from 'react';
import type { TokenAttribution } from '../../types/context-types';
import { formatPersonaName } from '../../utils/persona-display';

export interface AgentTokenSummary {
  personaName: string;
  tokenCount: number;
  cognitiveToolType?: string;
}

interface ContextUsageBarProps {
  /** Null renders nothing — the no-data state is the caller's (never a 0% bar). */
  usagePercent: number | null;
  attribution?: TokenAttribution;
  contextWindowSize?: number;
  compact?: boolean;
  pinCount?: number;
  /** Per-agent token breakdown for orchestration cost visibility. */
  agentTokens?: AgentTokenSummary[];
  /** Prompt tokens — when provided, the label renders "X / Y (Z%)" instead of
   * "Z% of Y" so prompt/window/percent each appear exactly once. */
  promptTokens?: number | null;
}

function getColorClass(percent: number): string {
  if (percent > 85) return 'bg-red-500';
  if (percent > 60) return 'bg-amber-500';
  return 'bg-green-500';
}

function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
  return String(tokens);
}

export function ContextUsageBar({ usagePercent, attribution, contextWindowSize, compact, pinCount, agentTokens, promptTokens }: ContextUsageBarProps) {
  const [showBreakdown, setShowBreakdown] = useState(false);

  if (usagePercent == null) return null;

  const percent = Math.min(100, Math.max(0, usagePercent));
  const colorClass = getColorClass(percent);

  return (
    <div data-testid="context-usage-bar" className="w-full">
      <button
        type="button"
        className="w-full text-left"
        onClick={() => setShowBreakdown(!showBreakdown)}
      >
        <div className="flex items-center gap-2">
          <div className={`flex-1 h-1.5 bg-muted rounded-full overflow-hidden ${compact ? 'max-w-[120px]' : ''}`}>
            <div
              className={`h-full rounded-full transition-all ${colorClass}`}
              style={{ width: `${percent}%` }}
            />
          </div>
          <span data-testid="context-usage-percent" className="text-[10px] text-muted-foreground whitespace-nowrap flex items-center gap-1">
            <span>
              {promptTokens != null && contextWindowSize
                ? `${formatTokens(promptTokens)} / ${formatTokens(contextWindowSize)} (${Math.round(percent)}%)`
                : `${Math.round(percent)}%`}
              {promptTokens == null && contextWindowSize && !compact ? ` of ${formatTokens(contextWindowSize)}` : ''}
            </span>
            {pinCount != null && pinCount > 0 && (
              <span className="inline-flex items-center gap-0.5">
                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="17" x2="12" y2="22" /><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
                </svg>
                <span>{pinCount}</span>
              </span>
            )}
          </span>
        </div>
      </button>

      {showBreakdown && attribution && (
        <div data-testid="context-usage-breakdown" className="mt-1.5 space-y-0.5 text-[10px] text-muted-foreground">
          {/* Estimates (assembler-derived) — labeled so a category is never read
              as an exact provider count. */}
          <div className="flex justify-between text-muted-foreground/60 italic">
            <span>breakdown (estimated)</span>
            <span />
          </div>
          <div className="flex justify-between">
            <span>System prompt</span>
            <span>{formatTokens(attribution.system_prompt_tokens)}</span>
          </div>
          <div className="flex justify-between">
            <span>Conversation</span>
            <span>{formatTokens(attribution.conversation_tokens)}</span>
          </div>
          <div className="flex justify-between">
            <span>Tool results</span>
            <span>{formatTokens(attribution.tool_result_tokens)}</span>
          </div>
          <div className="flex justify-between">
            <span>Tool schemas</span>
            <span>{formatTokens(attribution.tool_schema_tokens)}</span>
          </div>
          {attribution.parent_conclusion_tokens > 0 && (
            <div className="flex justify-between">
              <span>Parent conclusions</span>
              <span>{formatTokens(attribution.parent_conclusion_tokens)}</span>
            </div>
          )}
          {attribution.pinned_item_tokens > 0 && (
            <div className="flex justify-between">
              <span>Pinned items</span>
              <span>{formatTokens(attribution.pinned_item_tokens)}</span>
            </div>
          )}
        </div>
      )}

      {showBreakdown && agentTokens && agentTokens.length > 0 && (
        <div data-testid="agent-token-breakdown" className="mt-1.5 space-y-0.5 text-[10px] text-muted-foreground">
          <div className="font-medium text-foreground/80">Orchestration cost</div>
          {agentTokens.map((task, i) => (
            <div key={i} className="flex justify-between">
              <span className="truncate mr-2">
                {formatPersonaName(task.personaName)}
                {task.cognitiveToolType && (
                  <span className="ml-1 text-indigo-600 dark:text-indigo-400">({task.cognitiveToolType})</span>
                )}
              </span>
              <span className="shrink-0">{formatTokens(task.tokenCount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
